import React, { useState, useEffect } from 'react';
import './FacelessVideoFactory.css';
import AnalyticsWidget from './AnalyticsWidget';
import VideoReviewCard from './VideoReviewCard';
import ControlsPanel from './ControlsPanel';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';

const FacelessVideoFactory = () => {
  const [activeTab, setActiveTab] = useState('review');
  const [isAutoPilot, setIsAutoPilot] = useState(false);
  const [pendingVideos, setPendingVideos] = useState([]);
  const [platformStatus, setPlatformStatus] = useState({ youtube: false, tiktok: false, instagram: false });
  const [analyticsData, setAnalyticsData] = useState({
    videosProduced: 0,
    scheduledPosts: 0,
    engagementRate: 0
  });

  const [suggestions, setSuggestions] = useState([]);
  const [isGeneratingIdeas, setIsGeneratingIdeas] = useState(false);
  const [generationStep, setGenerationStep] = useState('');

  const fetchDashboardData = async () => {
    try {
      const [videosRes, analyticsRes, settingsRes, platformRes] = await Promise.all([
        fetch(`${API_BASE}/videos`),
        fetch(`${API_BASE}/analytics`),
        fetch(`${API_BASE}/settings`),
        fetch(`${API_BASE}/platforms/status`)
      ]);

      const videos = await videosRes.json();
      const analytics = await analyticsRes.json();
      const settings = await settingsRes.json();
      const platforms = await platformRes.json();

      setPendingVideos(videos.reverse());
      setAnalyticsData(analytics);
      setIsAutoPilot(settings.isAutoPilot);
      setPlatformStatus(platforms);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    }
  };

  const fetchSuggestions = async () => {
    setIsGeneratingIdeas(true);
    try {
      const res = await fetch(`${API_BASE}/ideas`);
      const ideas = await res.json();
      setSuggestions(ideas);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setIsGeneratingIdeas(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchSuggestions(); // Initial suggestions
    // Poll every 5 seconds for updates (e.g., cron jobs posting videos)
    const interval = setInterval(fetchDashboardData, 5000);
    return () => clearInterval(interval);
  }, []);

  const [isGenerating, setIsGenerating] = useState(false);

  const handleForceGenerate = async (topic = "") => {
    setIsGenerating(true);
    const steps = [
      "🧠 Analyzing topic & writing script...",
      "🎙️ Generating AI voiceover (ElevenLabs)...",
      "🖼️ Searching Pexels for relevant b-roll...",
      "🎬 Assembling final video render..."
    ];
    
    let stepIdx = 0;
    setGenerationStep(steps[0]);
    const stepInterval = setInterval(() => {
      stepIdx = (stepIdx + 1) % steps.length;
      setGenerationStep(steps[stepIdx]);
    }, 4000);

    try {
      const res = await fetch(`${API_BASE}/videos/generate`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic })
      });
      if (!res.ok) {
        const err = await res.json();
        alert(`Generation failed: ${err.details || err.error}`);
      } else {
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Failed to generate video:', error);
      alert('Failed to connect to backend.');
    } finally {
      clearInterval(stepInterval);
      setIsGenerating(false);
      setGenerationStep('');
      if (document.getElementById('topic-input')) {
        document.getElementById('topic-input').value = '';
      }
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Are you sure you want to clear the entire review queue? This cannot be undone.")) return;
    try {
      await fetch(`${API_BASE}/videos`, { method: 'DELETE' });
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to clear videos:', error);
    }
  };

  const handleToggleAutoPilot = async (newValue) => {
    try {
      setIsAutoPilot(newValue); // Optimistic UI update
      await fetch(`${API_BASE}/settings/autopilot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isAutoPilot: newValue })
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to toggle AutoPilot:', error);
    }
  };

  const metrics = [
    { label: 'Videos Produced Today', value: analyticsData.videosProduced, trend: '+25%', isPositive: true },
    { label: 'Scheduled Posts', value: analyticsData.scheduledPosts, trend: '+2', isPositive: true },
    { label: 'Avg Engagement Rate', value: `${analyticsData.engagementRate}%`, trend: '-0.5%', isPositive: false },
  ];

  return (
    <div className="fvf-dashboard">
      <div className="fvf-sidebar">
        <div className="fvf-brand">
          <span style={{ fontSize: '24px' }}>⚡️</span>
          Factory
        </div>

        <nav>
          <div 
            className={`fvf-nav-link ${activeTab === 'review' ? 'active' : ''}`}
            onClick={() => setActiveTab('review')}
          >
            📋 Review Queue
          </div>
          <div 
            className={`fvf-nav-link ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            📊 Analytics
          </div>
          <div 
            className={`fvf-nav-link ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            ⚙️ Settings
          </div>
          <div 
            className="fvf-nav-link"
            onClick={() => window.location.hash = '#home'}
            style={{ marginTop: 'auto' }}
          >
            🏠 Back to Portfolio
          </div>
        </nav>
      </div>

      <div className="fvf-main">
        <header className="fvf-header">
          <div>
            <h1>Dashboard</h1>
            <p style={{ color: '#8b949e', marginTop: '4px' }}>Manage your viral content pipeline</p>
          </div>
          <ControlsPanel 
            isAutoPilot={isAutoPilot} 
            setIsAutoPilot={handleToggleAutoPilot} 
          />
        </header>

        {activeTab === 'review' && (
          <>
            <div className="fvf-analytics-grid">
              {metrics.map((metric, idx) => (
                <AnalyticsWidget key={idx} data={metric} />
              ))}
            </div>

            <div style={{ marginBottom: '24px', background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px', border: '1px solid #30363d' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ color: '#8b949e', margin: '0 0 8px 0', fontSize: '12px' }}>What should the video be about?</p>
                  <input 
                    type="text" 
                    placeholder="e.g. The secret history of Bitcoin..."
                    style={{ width: '100%', background: 'transparent', border: 'none', color: 'white', fontSize: '1rem', outline: 'none' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleForceGenerate(e.target.value);
                    }}
                    id="topic-input"
                  />
                </div>
                <button 
                  onClick={() => handleForceGenerate(document.getElementById('topic-input').value)} 
                  className={`fvf-btn fvf-btn-primary ${isGenerating ? 'fvf-pulse' : ''}`}
                  style={{ padding: '12px 24px' }}
                  disabled={isGenerating}
                >
                  {isGenerating ? '⏳ Processing...' : '🚀 Generate'}
                </button>
              </div>

              {isGenerating && (
                <div style={{ marginTop: '12px', padding: '8px', background: 'rgba(121, 40, 202, 0.1)', borderRadius: '8px', border: '1px dashed rgba(121, 40, 202, 0.3)' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#d1b3ff', textAlign: 'center' }}>
                    {generationStep}
                  </p>
                </div>
              )}

              <div style={{ marginTop: '16px', borderTop: '1px solid #30363d', paddingTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <p style={{ color: '#8b949e', margin: 0, fontSize: '12px' }}>✨ AI World-Class Suggestions</p>
                  <button 
                    onClick={fetchSuggestions} 
                    disabled={isGeneratingIdeas}
                    style={{ background: 'none', border: 'none', color: '#58a6ff', fontSize: '12px', cursor: 'pointer', padding: 0 }}
                  >
                    {isGeneratingIdeas ? 'Refreshing...' : '🔄 Refresh Suggestions'}
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {suggestions.map((idea, i) => (
                    <div 
                      key={i} 
                      onClick={() => {
                        document.getElementById('topic-input').value = idea;
                      }}
                      style={{ 
                        background: 'rgba(88, 166, 255, 0.1)', 
                        border: '1px solid rgba(88, 166, 255, 0.3)',
                        borderRadius: '20px',
                        padding: '6px 14px',
                        fontSize: '12px',
                        color: '#58a6ff',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'rgba(88, 166, 255, 0.2)'}
                      onMouseOut={(e) => e.target.style.background = 'rgba(88, 166, 255, 0.1)'}
                    >
                      {idea}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ color: '#fff', fontSize: '1.25rem', margin: 0 }}>Review Queue ({pendingVideos.length})</h2>
              {pendingVideos.length > 0 && (
                <button 
                  onClick={handleClearAll}
                  style={{ 
                    background: 'rgba(239, 68, 68, 0.1)', 
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    color: '#ef4444',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.2)'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.1)'}
                >
                  🗑️ Clear Queue
                </button>
              )}
            </div>

            <div className="fvf-review-grid">
              {pendingVideos.map(video => (
                <VideoReviewCard key={video.id} video={video} onRefresh={fetchDashboardData} />
              ))}
            </div>

            {pendingVideos.length === 0 && (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px', 
                background: 'rgba(255,255,255,0.02)', 
                borderRadius: '16px', 
                border: '1px dashed #30363d',
                marginTop: '20px'
              }}>
                <span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>🎬</span>
                <h3 style={{ color: '#fff', margin: '0 0 8px 0' }}>Your Queue is Empty</h3>
                <p style={{ color: '#8b949e', margin: 0 }}>Generate a new video or wait for the autopilot to pick a topic!</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'analytics' && (
          <div className="fvf-glass-card">
            <h2 style={{ color: '#fff', marginTop: 0 }}>Detailed Analytics</h2>
            <p style={{ color: '#8b949e' }}>Historical performance data will appear here.</p>
            {/* Visualizer could go here */}
            <div style={{ height: '300px', border: '1px dashed #30363d', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#30363d' }}>
              Chart Placeholder
            </div>
          </div>
        )}
        
        {activeTab === 'settings' && (
          <div className="fvf-glass-card">
            <h2 style={{ color: '#fff', marginTop: 0 }}>Platform Connections</h2>
            <p style={{ color: '#8b949e', marginBottom: '24px' }}>Connect your social media accounts to enable publishing.</p>

            {[
              {
                key: 'youtube',
                emoji: '📺',
                name: 'YouTube Shorts',
                color: '#FF0000',
                steps: [
                  'Go to console.cloud.google.com → Enable YouTube Data API v3',
                  'Create OAuth 2.0 credentials (Web App), add redirect: http://localhost:4000/auth/youtube/callback',
                  'Add YOUTUBE_CLIENT_ID and YOUTUBE_CLIENT_SECRET to faceless-backend/.env',
                  'Run: node scripts/get_youtube_token.js (backend running) and follow the URL',
                  'Paste YOUTUBE_REFRESH_TOKEN into .env and restart backend'
                ]
              },
              {
                key: 'tiktok',
                emoji: '🎵',
                name: 'TikTok',
                color: '#69C9D0',
                steps: [
                  'Go to developers.tiktok.com → Create App with Login Kit + Content Posting API',
                  'Add redirect URI: http://localhost:4000/auth/tiktok/callback',
                  'Add yourself as a test user in the app settings',
                  'Add TIKTOK_CLIENT_KEY and TIKTOK_CLIENT_SECRET to .env',
                  'Run: node scripts/get_tiktok_token.js and follow the URL to get your TIKTOK_ACCESS_TOKEN'
                ]
              },
              {
                key: 'instagram',
                emoji: '📸',
                name: 'Instagram Reels',
                color: '#E1306C',
                steps: [
                  'Requires a Facebook Business account linked to an Instagram Business/Creator account',
                  'Go to developers.facebook.com → Create an App → Add Instagram Graph API product',
                  'Generate a long-lived access token from the Graph API Explorer',
                  'Add IG_USER_ID and IG_ACCESS_TOKEN to faceless-backend/.env',
                  'Restart the backend'
                ]
              }
            ].map(platform => (
              <div key={platform.key} style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${platformStatus[platform.key] ? platform.color + '55' : '#30363d'}`,
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: platformStatus[platform.key] ? 0 : '16px' }}>
                  <span style={{ fontSize: '28px' }}>{platform.emoji}</span>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ color: '#fff', margin: 0, fontSize: '1rem' }}>{platform.name}</h3>
                  </div>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    background: platformStatus[platform.key] ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)',
                    color: platformStatus[platform.key] ? '#10b981' : '#ef4444'
                  }}>
                    {platformStatus[platform.key] ? '✅ Connected' : '⚠️ Not Set Up'}
                  </span>
                </div>
                {!platformStatus[platform.key] && (
                  <ol style={{ color: '#8b949e', fontSize: '13px', margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                    {platform.steps.map((step, i) => <li key={i}>{step}</li>)}
                  </ol>
                )}
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default FacelessVideoFactory;
